import emitter from './emitter';
import { supabase, reinitSupabaseClient } from '@/lib/supabaseClient';
import { useUserStore } from '@/store/user';
import stateStore from './store';

// Type definitions
type AppState = 'active' | 'background';
type ElementSelector = string;
type EventListenerInfo = {
  element: Element | Window | Document;
  type: string;
  listener: EventListenerOrEventListenerObject;
  options?: boolean | AddEventListenerOptions;
};

// Global event listeners registry
const globalEventListeners: EventListenerInfo[] = [];

// Configuration
const CONFIG = {
  // How long to wait before considering the app to be in background state (in ms)
  DETECTION_TIMEOUT: 500,
  // Elements that need to be refreshed when app comes back from background
  PROBLEMATIC_SELECTORS: {
    CREATE_LEAD: '.create-lead',
    SIDEBAR_ACCORDION: '.sidebar-accordion',
    DROPDOWN_BUTTONS: '.dropdown-btn',
    ACTION_BUTTONS: '.action-button',
    TOP_MENU: '.top-menu-item',
  },
  // Pages that need data refresh when app returns from background
  PAGES_TO_REFRESH: {
    LEADS: '/leads',
    DEALS: '/deals',
    CUSTOMERS: '/customers',
    DASHBOARD: '/dashboard',
  },
};

// Global state variables
let currentState: AppState = 'active';
let lastActiveTime = Date.now();
let backgroundTimer: number | null = null;
let lastStateChangeTime = 0;
let isInitialized = false;
const STATE_CHANGE_DEBOUNCE_MS = 3000; // הגדלה ל-3 שניות למניעת שינויי מצב תכופים מדי

/**
 * Initialize the app state manager
 */
export function initAppStateManager() {
  if (isInitialized) {
    console.log('%c[AppStateManager] Already initialized, skipping...', 'color: #FF9800');
    return;
  }
  
  console.log('%c[AppStateManager] Initializing...', 'color: #4CAF50');
  
  // Set up visibility change detection
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  // Set up window blur/focus detection
  window.addEventListener('blur', handleWindowBlur);
  window.addEventListener('focus', handleWindowFocus);
  
  // Set up activity detection
  document.addEventListener('mousemove', updateActivity);
  document.addEventListener('mousedown', updateActivity);
  document.addEventListener('keypress', updateActivity);
  document.addEventListener('touchstart', updateActivity);
  document.addEventListener('scroll', updateActivity);
  
  // Set up periodic check
  setInterval(periodicCheck, 30000);
  
  isInitialized = true;
  console.log('%c[AppStateManager] Initialized', 'color: #4CAF50');
}

/**
 * Register a global event listener that will be tracked and can be refreshed
 */
export function registerGlobalEventListener(
  element: Element | Window | Document,
  type: string,
  listener: EventListenerOrEventListenerObject,
  options?: boolean | AddEventListenerOptions
) {
  element.addEventListener(type, listener, options);
  
  globalEventListeners.push({
    element,
    type,
    listener,
    options
  });
}

/**
 * Remove a global event listener from tracking
 */
export function unregisterGlobalEventListener(
  element: Element | Window | Document,
  type: string,
  listener: EventListenerOrEventListenerObject
) {
  element.removeEventListener(type, listener);
  
  const index = globalEventListeners.findIndex(
    info => info.element === element && info.type === type && info.listener === listener
  );
  
  if (index !== -1) {
    globalEventListeners.splice(index, 1);
  }
}

/**
 * Refresh all registered global event listeners
 */
function refreshGlobalEventListeners() {
  console.log('%c[AppStateManager] Refreshing global event listeners', 'color: #2196F3');
  
  globalEventListeners.forEach(({ element, type, listener, options }) => {
    try {
      element.removeEventListener(type, listener);
      element.addEventListener(type, listener, options);
    } catch (error) {
      console.error('[AppStateManager] Error refreshing event listener:', error);
    }
  });
}

/**
 * Handle document visibility change
 */
function handleVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    setBackgroundState();
  } else {
    setActiveState();
  }
}

/**
 * Handle window blur event
 */
function handleWindowBlur() {
  // Use setTimeout to avoid false positives
  if (backgroundTimer === null) {
    backgroundTimer = window.setTimeout(() => {
      setBackgroundState();
    }, CONFIG.DETECTION_TIMEOUT);
  }
}

/**
 * Handle window focus event
 */
function handleWindowFocus() {
  if (backgroundTimer !== null) {
    window.clearTimeout(backgroundTimer);
    backgroundTimer = null;
  }
  
  setActiveState();
}

/**
 * Update the last activity timestamp
 */
function updateActivity() {
  lastActiveTime = Date.now();
  
  // If we were in background state, transition back to active
  if (currentState === 'background') {
    setActiveState();
  }
}

/**
 * Periodic check to detect if the app is stuck
 */
function periodicCheck() {
  const now = Date.now();
  const timeSinceLastActivity = now - lastActiveTime;
  
  // If no activity for a long time, refresh UI components
  if (timeSinceLastActivity > 60000) { // 1 minute
    console.log('%c[AppStateManager] Long inactivity detected, refreshing components', 'color: #FF9800');
    fixProblematicElements();
  }
}

/**
 * Set the app state to background
 */
function setBackgroundState() {
  const now = Date.now();
  
  // Debouncing למניעת שינויי מצב תכופים מדי
  if (currentState === 'background') {
    console.log('%c[AppStateManager] Already in background state, skipping', 'color: #FF9800');
    return;
  }
  
  if ((now - lastStateChangeTime) < STATE_CHANGE_DEBOUNCE_MS) {
    console.log(`%c[AppStateManager] Skipping background state - too frequent (${now - lastStateChangeTime}ms < ${STATE_CHANGE_DEBOUNCE_MS}ms)`, 'color: #FF9800');
    return;
  }
  
  currentState = 'background';
  lastStateChangeTime = now;
  console.log('%c[AppStateManager] App went to background', 'color: #F44336');
  
  // Emit background event
  emitter.emit('app:background');
}

/**
 * Set the app state to active
 */
function setActiveState() {
  const now = Date.now();
  
  // Debouncing למניעת שינויי מצב תכופים מדי
  if (currentState === 'active') {
    console.log('%c[AppStateManager] Already in active state, skipping', 'color: #FF9800');
    return;
  }
  
  if ((now - lastStateChangeTime) < STATE_CHANGE_DEBOUNCE_MS) {
    console.log(`%c[AppStateManager] Skipping active state - too frequent (${now - lastStateChangeTime}ms < ${STATE_CHANGE_DEBOUNCE_MS}ms)`, 'color: #FF9800');
    return;
  }
  
  const previousState = currentState;
  currentState = 'active';
  lastStateChangeTime = now;
  console.log('%c[AppStateManager] App became active', 'color: #4CAF50');
  
  // Only do recovery if we were previously in background
  if (previousState === 'background') {
    recoverFromBackgroundState();
  }
  
  // Emit active event
  emitter.emit('app:active');
}

/**
 * Recover the app from background state
 */
function recoverFromBackgroundState() {
  console.log('%c[AppStateManager] Recovering from background state', 'color: #2196F3');
  
  // Fix problematic UI elements
  fixProblematicElements();
  
  // Refresh event listeners
  refreshGlobalEventListeners();
  
  // Refresh relevant data
  refreshDataForCurrentPage();
  
  // הסרת אתחול אוטומטי של Supabase client למניעת לולאות אינסופיות
  // במקום זה, הקומפוננטים יכולים לקרוא ל-reinitSupabaseClient() ידנית אם צריך
  console.log('%c[AppStateManager] Recovery completed (Supabase reinitialization removed to prevent loops)', 'color: #2196F3');
}

/**
 * Fix problematic UI elements after returning from background
 */
function fixProblematicElements() {
  // Replace problematic buttons
  replaceElements(CONFIG.PROBLEMATIC_SELECTORS.CREATE_LEAD);
  replaceElements(CONFIG.PROBLEMATIC_SELECTORS.ACTION_BUTTONS);
  replaceElements(CONFIG.PROBLEMATIC_SELECTORS.DROPDOWN_BUTTONS);
  
  // Fix sidebar accordion
  const accordionElements = document.querySelectorAll(CONFIG.PROBLEMATIC_SELECTORS.SIDEBAR_ACCORDION);
  accordionElements.forEach(element => {
    const clone = element.cloneNode(true);
    element.parentNode?.replaceChild(clone, element);
  });
  
  // Fix top menu
  const topMenuElements = document.querySelectorAll(CONFIG.PROBLEMATIC_SELECTORS.TOP_MENU);
  topMenuElements.forEach(element => {
    const clone = element.cloneNode(true);
    element.parentNode?.replaceChild(clone, element);
  });
  
  // Force stateStore update
  if (stateStore) {
    // Touch store to ensure reactivity - fix TypeScript error
    if (typeof stateStore.refresh === 'function') {
      stateStore.refresh();
    } else {
      console.log('StateStore refresh failed, no method available');
    }
  }
}

/**
 * Replace DOM elements with clones to refresh their event listeners
 */
function replaceElements(selector: ElementSelector) {
  const elements = document.querySelectorAll(selector);
  
  elements.forEach(element => {
    try {
      const clone = element.cloneNode(true);
      element.parentNode?.replaceChild(clone, element);
    } catch (error) {
      console.error(`[AppStateManager] Error replacing element ${selector}:`, error);
    }
  });
}

/**
 * Refresh data based on the current page
 */
function refreshDataForCurrentPage() {
  const currentPath = window.location.pathname;
  
  // Emitting events for specific pages to handle their own data refresh
  if (currentPath.includes(CONFIG.PAGES_TO_REFRESH.LEADS)) {
    emitter.emit('page:refresh:leads');
  } else if (currentPath.includes(CONFIG.PAGES_TO_REFRESH.DEALS)) {
    emitter.emit('page:refresh:deals');
  } else if (currentPath.includes(CONFIG.PAGES_TO_REFRESH.CUSTOMERS)) {
    emitter.emit('page:refresh:customers');
  } else if (currentPath.includes(CONFIG.PAGES_TO_REFRESH.DASHBOARD)) {
    emitter.emit('page:refresh:dashboard');
  }

  // Refresh user data if needed - using setTimeout to avoid Supabase deadlock
  const userStore = useUserStore();
  if (userStore.user) {
    setTimeout(async () => {
      try {
        if (supabase) {
          const { data } = await supabase.auth.getSession();
          if (data.session) {
            // Session is still valid, refresh organization settings
            userStore.fetchOrganizationSettings();
          }
        } else {
          console.error('[AppStateManager] Supabase client is null');
        }
      } catch (error) {
        console.error('[AppStateManager] Error refreshing session:', error);
      }
    }, 0);
  }
}

// Export a default object with all functions
export default {
  init: initAppStateManager,
  registerListener: registerGlobalEventListener,
  unregisterListener: unregisterGlobalEventListener,
}; 